// Mirrors src/screens/rescue/RescueChamberListScreen.tsx — search + 4
// summary cards + 2-column grid of chamber cards with name, status badge,
// location, and an occupancy bar colored by fill ratio.

import React, {useMemo, useState} from 'react'
import clsx from 'clsx'
import {useNavigate} from 'react-router-dom'
import {Plus} from 'lucide-react'
import {Dropdown} from '../components/common/Dropdown'
import {SearchBar} from '../components/common/SearchBar'
import {SummaryCard} from '../components/common/SummaryCard'
import {useSupabaseSubscription} from '../app/useSupabaseSubscription'
import type {ChamberDetail} from './chamber/types'

type ChamberStatus = 'available' | 'full' | 'near_capacity' | 'maintenance'

interface ChamberRow extends ChamberDetail {}

const STATUS_BADGE: Record<ChamberStatus, {bg: string; text: string; label: string}> = {
  available:     {bg: 'bg-badgeGreenBg',  text: 'text-statusGreen',  label: 'Available'},
  full:          {bg: 'bg-badgeRedBg',    text: 'text-statusRed',    label: 'Full'},
  near_capacity: {bg: 'bg-badgeOrangeBg', text: 'text-statusOrange', label: 'Near capacity'},
  maintenance:   {bg: 'bg-surfaceLight',  text: 'text-textSecondary', label: 'Maintenance'},
}

function occupancyColor(occ: number, cap: number): string {
  if (cap === 0) return 'var(--color-textMuted)'
  const ratio = occ / cap
  if (ratio < 0.5) return 'var(--color-statusGreen)'
  if (ratio < 0.9) return 'var(--color-statusBlue)'
  return 'var(--color-statusOrange)'
}

export const RescueChambersScreen: React.FC = () => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [level, setLevel] = useState('all')

  const {rows: chambers, loading, error} = useSupabaseSubscription<ChamberRow>({
    table: 'rescue_chambers',
    columns:
      'id, mine_id, name, location, location_description, capacity, current_occupancy, ' +
      'status, maintenance_status, last_inspection_date, installation_date, access_point, ' +
      'x, y, z, level',
    orderBy: ['name', true],
  })

  // Live chamber_personnel feed. We don't need each row's data here — only
  // the per-chamber count — but we subscribe so INSERT/DELETE updates the
  // occupancy displayed on each card without waiting for TagBoard mobile
  // to rewrite rescue_chambers.current_occupancy.
  interface ChamberPersonnelRow {
    id: string
    chamber_id: string
  }
  const {rows: cpRows} = useSupabaseSubscription<ChamberPersonnelRow>({
    table: 'chamber_personnel',
    columns: 'id, chamber_id',
    scopedToMine: false,
  })

  // Live occupancy = count of chamber_personnel rows per chamber_id. Falls
  // back to chamber.current_occupancy when the join table hasn't loaded yet.
  const liveOccupancyByChamber = useMemo(() => {
    const counts = new Map<string, number>()
    for (const cp of cpRows) {
      counts.set(cp.chamber_id, (counts.get(cp.chamber_id) ?? 0) + 1)
    }
    return counts
  }, [cpRows])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return chambers.filter(c => {
      if (level !== 'all' && c.location !== level) return false
      if (q && !(`${c.name} ${c.location} ${c.location_description ?? ''}`.toLowerCase().includes(q))) {
        return false
      }
      return true
    })
  }, [chambers, search, level])

  const summary = useMemo(() => {
    let total = chambers.length
    let full = 0
    let maintenance = 0
    let sheltered = 0
    for (const c of chambers) {
      if (c.status === 'full') full++
      if (c.status === 'maintenance') maintenance++
      // Prefer live join-table count; fall back to the cached column.
      sheltered += liveOccupancyByChamber.get(c.id) ?? c.current_occupancy
    }
    return {total, full, maintenance, sheltered}
  }, [chambers, liveOccupancyByChamber])

  // Build the level dropdown from distinct chamber locations. Match TagBoard's
  // Dropdown options shape: {label, value}.
  const levelOptions = useMemo(() => {
    const set = new Set<string>()
    for (const c of chambers) set.add(c.location)
    const sorted = Array.from(set).sort()
    return [
      {label: 'All levels', value: 'all'},
      ...sorted.map(l => ({label: l, value: l})),
    ]
  }, [chambers])

  if (loading) {
    return <div className="flex h-full items-center justify-center text-textMuted">Loading chambers…</div>
  }
  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-xxxl">
        <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl">
          <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
          <pre className="whitespace-pre-wrap font-mono text-sm text-textPrimary">{error}</pre>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      {/* Controls — searchRow inside surface card + Add button. */}
      <div className="px-xl py-md">
        <div className="flex items-center gap-lg">
          <div className="flex flex-1 items-center gap-lg rounded-lg bg-surface p-sm">
            <div className="flex-1">
              <SearchBar value={search} onChange={setSearch} placeholder="Search.." />
            </div>
            <Dropdown label="Level" value={level} onChange={setLevel} options={levelOptions} />
          </div>
          <button
            type="button"
            onClick={() => navigate('/chambers/add')}
            className="flex items-center gap-sm rounded-md bg-primary px-xl py-md text-md font-bold text-black hover:bg-primaryLight"
          >
            <Plus size={16} color="black" strokeWidth={3} />
            Add Chamber
          </button>
        </div>
      </div>

      {/* Summary — px-xl gap-md mb-lg. Left accent borders match the
          colored stripe on mobile RescueChamberListScreen summary cards. */}
      <div className="mb-lg grid grid-cols-4 gap-md px-xl">
        <SummaryCard
          label="Total"
          value={summary.total}
          accentColor="var(--color-primary)"
        />
        <SummaryCard
          label="Full"
          value={summary.full}
          valueClass="text-statusRed"
          accentColor="var(--color-statusRed)"
        />
        <SummaryCard
          label="Maintenance"
          value={summary.maintenance}
          valueClass="text-statusYellow"
          accentColor="var(--color-statusYellow)"
        />
        <SummaryCard
          label="Total Sheltered"
          value={summary.sheltered}
          valueClass="text-statusGreen"
          accentColor="var(--color-statusGreen)"
        />
      </div>

      {/* Grid — gridContent.px-lg pb-xxl. Chamber cards from mobile ChamberCard.tsx:
          bg-surfaceCard, rounded-lg, p-lg, border surfaceBorder, name xl/700,
          location sm-textSecondary mb-lg, 6px occupancy bar. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-lg pb-xxl">
        {filtered.length === 0 ? (
          <div className="flex h-full items-center justify-center text-textMuted">
            No chambers match the current filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-md md:grid-cols-2">
            {filtered.map(c => {
              const badge = STATUS_BADGE[c.status]
              const liveOcc = liveOccupancyByChamber.get(c.id) ?? c.current_occupancy
              const ratio = c.capacity > 0 ? liveOcc / c.capacity : 0
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => navigate(`/chambers/${c.id}`)}
                  className="rounded-lg border border-surfaceBorder bg-surfaceCard p-lg text-left hover:border-primary focus:border-primary focus:outline-none"
                >
                  <div className="mb-xs flex items-center justify-between">
                    <div className="text-xl font-bold text-textPrimary">{c.name}</div>
                    <span
                      className={clsx(
                        'rounded-round px-md py-xs text-xs font-semibold uppercase',
                        badge.bg,
                        badge.text,
                      )}
                    >
                      {badge.label}
                    </span>
                  </div>

                  <div className="mb-lg text-sm text-textSecondary">
                    {c.location_description || c.location}
                  </div>

                  <div className="mb-sm flex items-center justify-between">
                    <span className="text-sm text-textSecondary">Occupancy</span>
                    <span className="text-sm font-semibold text-textPrimary">
                      {liveOcc} / {c.capacity}
                    </span>
                  </div>
                  <div className="h-[6px] overflow-hidden rounded-[3px] bg-surfaceBorder">
                    <div
                      className="h-full rounded-[3px] transition-all"
                      style={{
                        width: `${Math.min(100, Math.round(ratio * 100))}%`,
                        backgroundColor: occupancyColor(liveOcc, c.capacity),
                      }}
                    />
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

    </div>
  )
}
